/**
 * Automation rules.
 *
 * A rule watches for one flag tag and, when a client raises it, can reassign
 * the owner, change the status, prepend a note, queue an AI draft and fire a
 * notification. Each rule fires once per client, then waits out its cooldown —
 * that bookkeeping lives in `astate.ruleRuns`, keyed `ruleId:clientId`.
 *
 * This module decides *what* should happen and hands back a plan; the store
 * applies it. Keeping the decision separate means the sweep can run on a timer
 * without dragging React state through the logic.
 */
import { deriveClient } from '../domain/derive.js';
import { mrrOf } from '../domain/pricing.js';
import { formatDate } from '../lib/dates.js';

/** Next owner in the round-robin, and the index to store back. */
export function nextOwner(S) {
  const owners = (S.owners || []).filter(Boolean);
  if (!owners.length) return { owner: '', rrIndex: S.rrIndex || 0 };
  const rrIndex = ((S.rrIndex || 0) + 1) % owners.length;
  return { owner: owners[rrIndex], rrIndex };
}

/**
 * Work out what the enabled rules want to do right now.
 *
 * Returns:
 *   patches   { [clientId]: partialRow }  field changes to apply
 *   drafts    [{ id, channel, why }]      AI drafts to queue
 *   notify    [alert-shaped objects]      desktop notifications to raise
 *   ruleRuns  { [key]: timestamp }        cooldown stamps to merge in
 *   rrIndex   number                      advanced round-robin pointer
 */
export function planRuleRun(S, rows, astate) {
  const rules = (S.rules || []).filter(r => r.enabled);
  const empty = { patches: {}, drafts: [], notify: [], ruleRuns: {}, rrIndex: S.rrIndex || 0, changed: false };
  if (!rules.length) return empty;

  const now = Date.now();
  const patches = {};
  const drafts = [];
  const notify = [];
  const ruleRuns = {};
  let rrIndex = S.rrIndex || 0;
  let changed = false;

  rows.forEach(r => {
    const d = deriveClient(S, r);
    if (d.excluded) return;

    d.flags.forEach(flag => {
      rules.filter(rule => rule.on === flag.tag).forEach(rule => {
        const key = `${rule.id}:${r.id}`;
        const last = astate.ruleRuns[key];
        if (last && (now - last) < (rule.cooldown || 7) * 864e5) return;

        ruleRuns[key] = now;
        changed = true;
        const patch = patches[r.id] || {};

        if (rule.assign === '__rr' && !String(r.handler || '').trim()) {
          const next = nextOwner({ ...S, rrIndex });
          rrIndex = next.rrIndex;
          patch.handler = next.owner;
        } else if (rule.assign && rule.assign !== '__rr') {
          patch.handler = rule.assign;
        }

        if (rule.status) patch.status = rule.status;

        if (rule.note) {
          const stamp = formatDate(new Date(), false, S.dateFmt);
          patch.notes = `${stamp} — ${rule.note}\n${patch.notes ?? r.notes ?? ''}`.slice(0, 900);
        }

        if (rule.draft && !r.outbox && S.aiOn !== false) {
          drafts.push({ id: r.id, channel: rule.channel || 'email', why: rule.name });
        }

        if (rule.notify) {
          notify.push({
            id: key,
            client: r.name,
            txt: `${rule.name} — ${flag.txt}`,
            tag: flag.tag,
            sev: flag.sev,
            value: Math.round(mrrOf(S, r, d))
          });
        }

        if (Object.keys(patch).length) patches[r.id] = patch;
      });
    });
  });

  return { patches, drafts, notify, ruleRuns, rrIndex, changed };
}

/**
 * Which scheduled jobs are due right now? The ledger only runs them while it
 * is open — the server's own cron covers the rest.
 */
export function dueJobs(S, { briefDate, digestDate, risking }) {
  const today = formatDate(new Date(), false, S.dateFmt);
  const hour = new Date().getHours();
  const aiOn = S.aiOn !== false;

  return {
    digest: !!(S.digestOn && aiOn && hour >= (S.digestHour || 9) && digestDate !== today),
    risk: !!(S.autoRisk && aiOn && hour >= (S.riskHour || 2) && S.lastRisk !== today && !risking),
    brief: !!(S.autoBrief && aiOn && briefDate !== today),
    today
  };
}

/* ------------------------------------------------------------------ Slack */

/** Post to an incoming webhook. Browsers cannot read the response, hence no-cors. */
export async function postSlack(S, text) {
  if (!S.slackWebhook) throw new Error('Add a Slack webhook in Settings → Automation');
  await fetch(S.slackWebhook, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text })
  });
}

/** A compact Slack-formatted summary of the top alerts. */
export function slackSummary(S, alerts, mrr, atRisk) {
  const currency = S.currency || 'AED';
  const m = v => `${currency} ${Math.round(v || 0).toLocaleString('en-US')}`;
  const head = `*${S.workspace || 'Client Master'} — ${formatDate(new Date(), false, S.dateFmt)}*`;
  const line = `MRR ${m(mrr)} · ${m(atRisk)} at risk · ${alerts.length} open alerts`;
  const items = alerts.slice(0, 8)
    .map(a => `• *${a.client}* — ${a.txt}${a.value ? ` (${m(a.value)}/mo)` : ''}`);
  return [head, line, ...items].join('\n');
}
